/**
 * MED-781: Aggregate the legacy "Action Items" sources (overdue attendance,
 * Calendly, midpoint/endpoint feedback) into structured descriptors that drive
 * Needs Attention rail cards. Replaces the ActionItemsPanel side panel — its
 * three data sources continue to be the source of truth for those cards
 * (per AC4, the panel is removed but its contents migrate into the rail).
 */

import { useEffect, useMemo, useState } from 'react';
import coachPortalApi from '../../../services/api/coachPortal';
import type { CoachCohort } from '../../../services/api/coachPortal';
import type { CalendlyBookingHealth, MissingMeetingLink } from '../../../types';
import {
  blockingQuestions,
  twentyMinuteNeedsMapping,
} from '../../../components/coaches/calendlyDurations';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { composeCoachSessionLabel } from '../utils/sessionLabel';

export interface ActionItemDescriptor {
  id: string;
  type:
    | 'overdue_attendance'
    | 'post_session_feedback'
    | 'connect_calendly'
    | 'calendly_booking_blocked'
    | 'meeting_link_missing'
    | 'calendly_20min_unmapped'
    | 'feedback';
  label: string;
  // MED-1363: set on calendly_booking_blocked only.
  calendlyProblem?: 'required_question';
  eventTypeName?: string;
  // MED-1365 QA round 6: set on meeting_link_missing only.
  meetingLink?: MissingMeetingLink;
  // MED-1365 QA round 9: set on calendly_20min_unmapped only.
  planStage?: CalendlyBookingHealth['plan_stage'];
  offTwentyNames?: string[];
  cohortId?: string;
  cohortName?: string;
  sessionId?: string;
  groupName?: string;
  surveyType?: 'midpoint' | 'endpoint';
  scheduledEnd?: string;
  referenceDate?: string;
  // MED-1147: participant display-name override + server-computed divergence
  // boolean, carried through to the session-derived Needs Attention cards so the
  // internal-awareness indicator can render. Undefined for non-session items
  // (Calendly, survey feedback).
  participantDisplayName?: string | null;
  participantDisplayDiffers?: boolean;
}

interface UseCoachActionItemsParams {
  cohorts: CoachCohort[];
  isCohortsLoading: boolean;
}

interface UseCoachActionItemsResult {
  items: ActionItemDescriptor[];
  isLoading: boolean;
  isCalendlyConnected: boolean;
}

export function useCoachActionItems({
  cohorts,
  isCohortsLoading,
}: UseCoachActionItemsParams): UseCoachActionItemsResult {
  const [items, setItems] = useState<ActionItemDescriptor[]>([]);
  const [isCalendlyConnected, setIsCalendlyConnected] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // MED-867 AC6: the post-session feedback card and its query are gated by the
  // live_session_delivery flag. Gating the fetch here (not just the backend)
  // means the endpoint isn't called at all when the flag is off — "query not
  // run". useFeatureFlag fails closed while flags load.
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  // Stable cohort key so the effect doesn't re-fire on every parent re-render
  // (e.g. when sessions or stats arrive). Cohort identity here is what drives
  // the per-cohort feedback fetches.
  const cohortKey = useMemo(() => cohorts.map(c => c.id).join('|'), [cohorts]);

  useEffect(() => {
    if (isCohortsLoading) return;

    let cancelled = false;
    setIsLoading(true);

    const run = async () => {
      const next: ActionItemDescriptor[] = [];

      // Calendly status — drives the Connect Calendly card (AC13).
      let calendlyConnected = true;
      try {
        const status = await coachPortalApi.getCalendlyStatus();
        calendlyConnected = status.connected;
        if (!status.connected) {
          next.push({
            id: 'calendly-connect',
            type: 'connect_calendly',
            label: 'Connect Calendly',
          });
        }
        // MED-1363: a required question stops Calendly from taking bookings.
        // MED-1365: a free or trial plan is not an item. Participants still
        // book online, through a Google Calendar invite from Medley.
        const health = status.connected ? status.booking_health : null;
        // MED-1365 QA round 9: only the questions that the profile card shows.
        const questions = blockingQuestions(health);
        if (questions.length > 0) {
          next.push({
            id: 'calendly-booking-blocked-question',
            type: 'calendly_booking_blocked',
            label: 'Calendly booking question',
            calendlyProblem: 'required_question',
            eventTypeName: questions
              .map(q => q.event_type_name)
              .join('" and "'),
          });
        }
        // MED-1365 QA round 9: the 20 min length has no usable event type.
        if (health && twentyMinuteNeedsMapping(health)) {
          next.push({
            id: 'calendly-20min-unmapped',
            type: 'calendly_20min_unmapped',
            label: 'Map a 20 min Calendly event type',
            planStage: health.plan_stage,
            offTwentyNames: health.off_20min_event_types ?? [],
          });
        }
        // MED-1365 QA round 6: a cohort's platform with no link on the profile.
        for (const link of status.missing_meeting_links ?? []) {
          next.push({
            id: `meeting-link-missing-${link.platform}`,
            type: 'meeting_link_missing',
            label: 'Meeting link',
            meetingLink: link,
          });
        }
      } catch {
        // Calendly not configured — treat as connected so we don't nag the coach.
        calendlyConnected = true;
      }

      // Per-cohort midpoint/endpoint feedback — drives Feedback cards (AC14).
      for (const cohort of cohorts) {
        for (const surveyType of ['midpoint', 'endpoint'] as const) {
          try {
            const data = await coachPortalApi.getCoachFeedback(cohort.id, surveyType);
            if (data.config && data.config.schedule_status === 'sent' && !data.submission) {
              next.push({
                id: `feedback-${cohort.id}-${surveyType}`,
                type: 'feedback',
                label: surveyType === 'midpoint' ? 'Midpoint Feedback' : 'Endpoint Feedback',
                cohortId: cohort.id,
                cohortName: cohort.name,
                surveyType,
              });
            }
          } catch {
            // No config for this surveyType — skip.
          }
        }
      }

      // Pending attendance — drives the Overdue Attendance card (AC10). The
      // legacy rule (delivered by getPendingAttendance) is preserved as-is per
      // the ticket's "no change to the rule that determines overdue".
      try {
        const attendance = await coachPortalApi.getPendingAttendance();
        for (const session of attendance.sessions) {
          next.push({
            id: `attendance-${session.id}`,
            type: 'overdue_attendance',
            // MED-1106: one shared label rule (topic → "Session N: {topic}",
            // else "Session N: {group}", else stored title).
            label: composeCoachSessionLabel({
              session_number: session.session_number,
              topic_name: session.topic_name,
              group_name: session.group_name,
              title: session.title,
            }),
            cohortId: session.cohort_id,
            cohortName: session.cohort_name,
            sessionId: session.id,
            groupName: session.group_name ?? undefined,
            scheduledEnd: session.scheduled_end,
            // MED-1147: carry divergence data to the Overdue Attendance card.
            participantDisplayName: session.topic_display_name,
            participantDisplayDiffers: session.participant_display_differs,
          });
        }
      } catch {
        // Silent — no pending attendance to surface.
      }

      // MED-867: Pending post-session feedback — drives the "Submit Session
      // Feedback" card. Multi-item (one descriptor per outstanding session),
      // sourced from the aggregated pending-feedback endpoint (AC1), not the
      // single-slot most-recent-completed source. Only fetched when the flag is
      // on (AC6); the backend also fails closed.
      if (liveSessionDeliveryEnabled) {
        try {
          const pendingFeedback = await coachPortalApi.getPendingFeedback();
          for (const session of pendingFeedback.sessions) {
            // MED-1181 / AC2: per-cohort LSD gate — only surface the feedback
            // card for a session whose cohort has Live Session Delivery on.
            // Fails closed for a classic MED01 cohort.
            if (!session.live_session_delivery_enabled) continue;
            next.push({
              id: `feedback-pending-${session.id}`,
              type: 'post_session_feedback',
              // MED-1106: one shared label rule (see Overdue Attendance above).
              label: composeCoachSessionLabel({
                session_number: session.session_number,
                topic_name: session.topic_name,
                group_name: session.group_name,
                title: session.title,
              }),
              cohortId: session.cohort_id,
              cohortName: session.cohort_name,
              sessionId: session.id,
              groupName: session.group_name ?? undefined,
              scheduledEnd: session.scheduled_end,
              // MED-1147: carry divergence data to the Submit Session Feedback card.
              participantDisplayName: session.topic_display_name,
              participantDisplayDiffers: session.participant_display_differs,
            });
          }
        } catch {
          // Silent — no pending feedback to surface (or flag off → 404).
        }
      }

      if (!cancelled) {
        setItems(next);
        setIsCalendlyConnected(calendlyConnected);
        setIsLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [cohorts, cohortKey, isCohortsLoading, liveSessionDeliveryEnabled]);

  return { items, isLoading, isCalendlyConnected };
}
